import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  limit as firestoreLimit,
  serverTimestamp,
  Timestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';

/**
 * Audit Service
 * 
 * Manages audit logging for all administrative actions in the Trilogy system.
 * Tracks who did what, when, and what changed.
 */

/**
 * Creates an audit log entry
 * @param {string} action - Action type (CREATE, UPDATE, DELETE, PUBLISH)
 * @param {string} entityType - Type of entity (question, version)
 * @param {string} entityId - ID of the entity
 * @param {string} adminId - Admin user ID
 * @param {string} adminEmail - Admin email
 * @param {Object} before - State before change (null for CREATE)
 * @param {Object} after - State after change (null for DELETE)
 * @returns {Promise<Object>} Created audit log
 */
export async function createAuditLog(
  action,
  entityType,
  entityId,
  adminId,
  adminEmail,
  before = null,
  after = null
) {
  if (!db) {
    throw new Error('Firestore is not configured');
  }

  try {
    // Compute changed fields
    const changes = [];
    if (before && after) {
      Object.keys(after).forEach(key => {
        if (JSON.stringify(before[key]) !== JSON.stringify(after[key])) {
          changes.push(key);
        }
      });
    }

    const auditLog = {
      action,
      entityType,
      entityId,
      adminId,
      adminEmail,
      timestamp: serverTimestamp(),
      before: before ? sanitizeForStorage(before) : null,
      after: after ? sanitizeForStorage(after) : null,
      changes
    };

    const docRef = await addDoc(collection(db, 'audit_logs'), auditLog);

    return {
      id: docRef.id,
      ...auditLog,
      timestamp: new Date()
    };
  } catch (error) {
    console.error('Error creating audit log:', error);
    throw error;
  }
}

/**
 * Sanitizes data for storage (removes functions, undefined values)
 * @param {Object} data - Data to sanitize
 * @returns {Object} Sanitized data
 */
function sanitizeForStorage(data) {
  if (!data) return null;
  
  return JSON.parse(JSON.stringify(data, (key, value) => {
    // Convert Firestore Timestamps to ISO strings
    if (value instanceof Timestamp) {
      return value.toDate().toISOString();
    }
    // Convert Dates to ISO strings
    if (value instanceof Date) {
      return value.toISOString();
    }
    return value;
  }));
}

/**
 * Gets audit logs with optional filtering
 * @param {Object} filters - Filter options
 * @param {string} filters.adminId - Filter by admin ID
 * @param {string} filters.entityType - Filter by entity type
 * @param {string} filters.action - Filter by action
 * @param {string} filters.entityId - Filter by entity ID
 * @param {Date} filters.startDate - Filter by start date
 * @param {Date} filters.endDate - Filter by end date
 * @param {number} filters.limit - Limit number of results
 * @returns {Promise<Array>} Array of audit logs
 */
export async function getAuditLogs(filters = {}) {
  if (!db) {
    console.warn('Firestore is not configured');
    return [];
  }

  try {
    const logsRef = collection(db, 'audit_logs');
    const constraints = [];

    // Add filters
    if (filters.adminId) {
      constraints.push(where('adminId', '==', filters.adminId));
    }
    if (filters.entityType) {
      constraints.push(where('entityType', '==', filters.entityType));
    }
    if (filters.action) {
      constraints.push(where('action', '==', filters.action));
    }
    if (filters.entityId) {
      constraints.push(where('entityId', '==', filters.entityId));
    }

    // Always order by timestamp descending
    constraints.push(orderBy('timestamp', 'desc'));

    // Add limit
    if (filters.limit) {
      constraints.push(firestoreLimit(filters.limit));
    }

    const q = query(logsRef, ...constraints);
    const snapshot = await getDocs(q);

    let logs = snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data()
    }));

    // Apply date filters in memory (Firestore doesn't support range queries with other filters easily)
    if (filters.startDate) {
      logs = logs.filter(log => {
        const logDate = log.timestamp?.toDate?.() || new Date(log.timestamp);
        return logDate >= filters.startDate;
      });
    }
    if (filters.endDate) {
      logs = logs.filter(log => {
        const logDate = log.timestamp?.toDate?.() || new Date(log.timestamp);
        return logDate <= filters.endDate;
      });
    }

    return logs;
  } catch (error) {
    console.error('Error getting audit logs:', error);
    throw error;
  }
}

/**
 * Gets audit logs for a specific entity
 * @param {string} entityType - Entity type
 * @param {string} entityId - Entity ID
 * @returns {Promise<Array>} Array of audit logs
 */
export async function getEntityAuditHistory(entityType, entityId) {
  return getAuditLogs({ entityType, entityId });
}

/**
 * Gets recent audit logs
 * @param {number} limit - Number of logs to retrieve
 * @returns {Promise<Array>} Array of recent audit logs
 */
export async function getRecentAuditLogs(limit = 100) {
  return getAuditLogs({ limit });
}

/**
 * Gets audit logs for a specific admin
 * @param {string} adminId - Admin user ID
 * @param {number} limit - Number of logs to retrieve
 * @returns {Promise<Array>} Array of audit logs
 */
export async function getAdminAuditLogs(adminId, limit = 100) {
  return getAuditLogs({ adminId, limit });
}

/**
 * Helper function to log question creation
 * @param {string} questionId - Question ID
 * @param {string} adminId - Admin user ID
 * @param {string} adminEmail - Admin email
 * @param {Object} questionData - Question data
 * @returns {Promise<Object>} Audit log
 */
export async function logQuestionCreated(questionId, adminId, adminEmail, questionData) {
  return createAuditLog(
    'CREATE',
    'question',
    questionId,
    adminId,
    adminEmail,
    null,
    questionData
  );
}

/**
 * Helper function to log question update
 * @param {string} questionId - Question ID
 * @param {string} adminId - Admin user ID
 * @param {string} adminEmail - Admin email
 * @param {Object} before - State before update
 * @param {Object} after - State after update
 * @returns {Promise<Object>} Audit log
 */
export async function logQuestionUpdated(questionId, adminId, adminEmail, before, after) {
  return createAuditLog(
    'UPDATE',
    'question',
    questionId,
    adminId,
    adminEmail,
    before,
    after
  );
}

/**
 * Helper function to log question deletion
 * @param {string} questionId - Question ID
 * @param {string} adminId - Admin user ID
 * @param {string} adminEmail - Admin email
 * @param {Object} questionData - Question data before deletion
 * @returns {Promise<Object>} Audit log
 */
export async function logQuestionDeleted(questionId, adminId, adminEmail, questionData) {
  return createAuditLog(
    'DELETE',
    'question',
    questionId,
    adminId,
    adminEmail,
    questionData,
    null
  );
}

/**
 * Helper function to log version publication
 * @param {string} versionId - Version ID
 * @param {string} adminId - Admin user ID
 * @param {string} adminEmail - Admin email
 * @param {Object} versionData - Version data
 * @returns {Promise<Object>} Audit log
 */
export async function logVersionPublished(versionId, adminId, adminEmail, versionData) {
  return createAuditLog(
    'PUBLISH',
    'version',
    versionId,
    adminId,
    adminEmail,
    null,
    {
      version: versionData.version,
      questionCount: versionData.questionsSnapshot?.length || 0,
      changesSummary: versionData.changesSummary
    }
  );
}
